(function () {
  "use strict";

  const CHECKOUT_REDIRECT_LOCK_TIMEOUT_MS = 15000;
  const CART_REQUEST_MAX_ATTEMPTS = 3;
  const CART_REQUEST_RETRY_DELAYS_MS = [200, 400];
  // Transient failures
  const RETRYABLE_STATUS_CODES = new Set([408, 409, 425, 429, 500, 502, 503, 504]);

  const cartRoutes = {
    ADD: "/cart/add.js",
    UPDATE: "/cart/update.js",
  };

  function delay(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function addSkipShopPayParam(urlOrPath) {
    const url = urlOrPath.startsWith("http")
      ? new URL(urlOrPath)
      : new URL(urlOrPath, window.location.origin);

    url.searchParams.set("skip_shop_pay", "true");

    return url.toString();
  }

  // In-memory discount cache (fast path)
  let discountCache = null;
  let discountCacheAt = 0;
  const DISCOUNT_CACHE_TTL_MS = 60 * 1000; // 60s is usually enough
  let cartMutationQueue = Promise.resolve();

  async function runCartMutation(task) {
    const next = cartMutationQueue.then(async () => task());
    cartMutationQueue = next.catch(() => undefined);
    return next;
  }

  // Fetch with retry: retries network failures and transient status codes
  async function fetchWithRetry(url, options) {
    let lastNetworkError = null;

    for (let attempt = 1; attempt <= CART_REQUEST_MAX_ATTEMPTS; attempt += 1) {
      let res;

      try {
        res = await fetch(url, options);
      } catch (err) {
        lastNetworkError = err;

        if (attempt < CART_REQUEST_MAX_ATTEMPTS) {
          await delay(CART_REQUEST_RETRY_DELAYS_MS[attempt - 1]);
          continue;
        }

        throw lastNetworkError;
      }

      const isRetryableStatus = RETRYABLE_STATUS_CODES.has(res.status);

      if (res.ok || !isRetryableStatus) {
        return res;
      }

      if (attempt < CART_REQUEST_MAX_ATTEMPTS) {
        await delay(CART_REQUEST_RETRY_DELAYS_MS[attempt - 1]);
        continue;
      }

      return res;
    }
  }

  async function postJSON(url, payload) {
    const res = await fetchWithRetry(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload || {}),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (_) {}

    if (res.ok) {
      return data;
    }

    console.error("Cart request failed:", url, data);
    throw new Error((data && data.description) || `Request failed: ${url}`);
  }

  async function fetchCurrentCartDiscounts() {
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      if (!res.ok) return [];
      const cart = await res.json();
      const discounts = cart.discount_codes?.map((d) => d.code);
      return discounts || [];
    } catch (err) {
      console.error("Error fetching cart:", err);
      return [];
    }
  }

  async function getCartDiscountsCached() {
    const now = Date.now();
    if (discountCache && now - discountCacheAt < DISCOUNT_CACHE_TTL_MS) {
      return discountCache;
    }
    const fresh = await fetchCurrentCartDiscounts();
    discountCache = fresh;
    discountCacheAt = now;
    return fresh;
  }

  function isCartWithItems(href) {
    if (!href) return false;
    return /\/cart\/\d+:\d+/.test(href);
  }

  function parseCartItems(pathname) {
    const cartPart = pathname.split("/cart/")[1];
    if (!cartPart) return [];
    return cartPart.split(",").map((item) => {
      const [id, qty, sellingPlan] = item.split(":");

      return {
        id,
        quantity: parseInt(qty || "1", 10),
        sellingPlan,
      };
    });
  }

  function hasSellingPlanItems(pathname) {
    const cartPart = pathname.split("/cart/")[1];
    if (!cartPart) return false;

    return cartPart.split(",").some((item) => {
      const parts = item.split(":");
      return parts.length >= 3 && Boolean(parts[2]);
    });
  }

  function makeBundleId() {
    if (window.crypto?.randomUUID) return `b_${crypto.randomUUID()}`;
    return `b_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  function shouldIgnoreClick(e, anchor) {
    if (e.defaultPrevented) return true;
    if (typeof e.button === "number" && e.button !== 0) return true;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return true;
  
    const href = anchor?.getAttribute("href");
  
    if (!href) return true;
  
    try {
      const url = new URL(href, window.location.origin);
  
      const allowedHostnames = new Set([
        "audienhearing.com",
        "www.audienhearing.com",
      ]);
  
      if (!allowedHostnames.has(url.hostname.toLowerCase())) {
        return true;
      }
    } catch {
      return true;
    }
  
    return false;
  }

  function setCheckoutInFlight(anchor, state) {
    if (!anchor) return;
    anchor.dataset.checkoutInFlight = state ? "1" : "";
    anchor.classList.toggle("is-checkout-in-flight", Boolean(state));
  }

  function isCheckoutInFlight(anchor) {
    return anchor?.dataset?.checkoutInFlight === "1";
  }

  function setDrawerLoading(drawer, isLoading) {
    if (!drawer) return;

    if (typeof drawer.setLoading === "function") {
      drawer.setLoading(Boolean(isLoading));
      return;
    }

    drawer.classList.toggle("is-loading", Boolean(isLoading));
  }

  // Add items to cart and (optionally) apply discounts.

  async function addItemsAndApplyDiscounts(
    dataAdd,
    newDiscountRaw,
    discountsPromise,
    skipExistingDiscountLookup = false,
  ) {
    await postJSON(cartRoutes.ADD, dataAdd);

    let cartAfter = null;

    try {
      if (newDiscountRaw) {
        let allDiscounts = new Set();
        if (!skipExistingDiscountLookup && window.cartType === "cart_drawer") {
          const cartDiscounts = discountsPromise
            ? await discountsPromise
            : await getCartDiscountsCached();

          allDiscounts = new Set(cartDiscounts);
        }
        newDiscountRaw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((code) => allDiscounts.add(code));

        const discountStr = Array.from(allDiscounts).join(",");

        if (discountStr) {
          cartAfter = await postJSON(cartRoutes.UPDATE, {
            discount: discountStr,
          });

          discountCache = Array.from(allDiscounts);
          discountCacheAt = Date.now();
        }
      }
    } catch (err) {
      err.itemsAddedToCart = true;
      throw err;
    }

    return cartAfter;
  }

  async function handleClick(e, anchor) {
    const href = anchor.getAttribute("href");

    // Intercept links with data-direct-checkout attr
    let isDirectCheckout = anchor.hasAttribute("data-direct-checkout");
    console.log("isDirectCheckout", isDirectCheckout);
    if (window.cartType === "cart_page" && anchor.pathname.includes("/cart/")) {
      isDirectCheckout = true;
    }

    if (!isCartWithItems(href) && !isDirectCheckout) return;

    if (isCheckoutInFlight(anchor)) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return;
    }

    setCheckoutInFlight(anchor, true);

    let url;

    try {
      url = href.startsWith("http")
        ? new URL(href)
        : new URL(href, window.location.origin);
    } catch {
      return;
    }

    const isCartItemsLink = isCartWithItems(href);
    const hasSellingPlan = hasSellingPlanItems(url.pathname);

    /**
     * Native direct checkout flow:
     * If the link does not contain selling plan data, do not run cart requests.
     * Cart drawer is excluded from this because cart drawer should always use
     * the fetch-based cart flow instead of navigating directly to checkout.
     */
    if (!hasSellingPlan && window.cartType !== "cart_drawer") {
      if (isCartItemsLink || isDirectCheckout) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        window.location.href = addSkipShopPayParam(url.toString());
      }

      return;
    }

    /**
     * Custom fetch-based flow:
     * Cart drawer links and selling plan links should reach this point.
     */
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const unlockTimer = window.setTimeout(() => {
      setCheckoutInFlight(anchor, false);
    }, CHECKOUT_REDIRECT_LOCK_TIMEOUT_MS);

    const items = parseCartItems(url.pathname);

    if (!items.length) {
      window.clearTimeout(unlockTimer);
      setCheckoutInFlight(anchor, false);
      return;
    }

    const mainProductId = url.searchParams.get("main-product-id");
    const newDiscountRaw = (url.searchParams.get("discount") || "").trim();

    // Additional discount props
    const additionalDiscountText = (
      url.searchParams.get("additional-discount-text") || ""
    ).trim();
    const additionalDiscountPriceRaw = (
      url.searchParams.get("additional-discount-price") || ""
    ).trim();
    const additionalDiscountPrice =
      additionalDiscountPriceRaw !== ""
        ? String(parseInt(additionalDiscountPriceRaw, 10))
        : "";

    const hasAdditionalDiscount =
      Boolean(additionalDiscountText) && Boolean(additionalDiscountPrice);

    const displayTitle = (url.searchParams.get("display-title") || "").trim();

    const isMulti = items.length > 1;

    // Decide which item is "main": matching main-product-id, otherwise fallback to first
    let mainIndex = -1;

    if (mainProductId) {
      mainIndex = items.findIndex(
        (it) => String(it.id) === String(mainProductId),
      );
    }

    if (mainIndex === -1) mainIndex = 0;

    const bundleId = isMulti ? makeBundleId() : null;

    const dataAdd = {
      items: items.map((it, idx) => {
        const isMain = idx === mainIndex;

        // Properties only exist when needed
        const props = {};

        if (isMulti) {
          props._product_type = isMain ? "main" : "bundle";
          props._bundle_id = bundleId;
        }

        if (isMain && hasAdditionalDiscount) {
          props._additional_discount_text = additionalDiscountText;
          props._additional_discount_price = additionalDiscountPrice;
        }

        if (isMain && displayTitle) {
          props._display_title = displayTitle;
        }

        const base = {
          id: it.id,
          quantity: it.quantity,
        };

        if (it.sellingPlan) {
          base.selling_plan = it.sellingPlan;
        }

        if (!isMulti && !Object.keys(props).length) return base;

        return {
          ...base,
          properties: props,
        };
      }),
    };

    const drawer = document.querySelector("wlsn-cart-drawer");

    if (isDirectCheckout) {
      try {
        await runCartMutation(async () => {
          await clearCart();
          await addItemsAndApplyDiscounts(dataAdd, newDiscountRaw, null, true);
        });

        window.location.href = addSkipShopPayParam("/checkout");
        return;
      } catch (err) {
        console.error("Error adding items for direct checkout:", err);

        // Server-side fallback. If /cart/add.js never succeeded, Shopify's
        // native cart permalink route can add the items itself
        window.location.href = err.itemsAddedToCart
          ? "/cart"
          : addSkipShopPayParam(href);
        return;
      } finally {
        setCheckoutInFlight(anchor, false);
        window.clearTimeout(unlockTimer);
      }
    }

    if (drawer && typeof drawer.openDrawer === "function") {
      drawer.openDrawer();
    }

    setDrawerLoading(drawer, true);

    const discountsPromise = newDiscountRaw
      ? getCartDiscountsCached()
      : Promise.resolve([]);

    try {
      const cartAfter = await runCartMutation(async () => {
        return await addItemsAndApplyDiscounts(
          dataAdd,
          newDiscountRaw,
          discountsPromise,
        );
      });

      // 3) Refresh drawer (pass cart if we have it to avoid /cart.js)
      if (drawer && typeof drawer.refreshDrawer === "function") {
        try {
          if (cartAfter) {
            await drawer.refreshDrawer({ cart: cartAfter });
          } else {
            await drawer.refreshDrawer();
          }
          drawer.openDrawer?.();
        } catch (refreshErr) {
          console.error("Error refreshing cart drawer:", refreshErr);

          window.location.href = "/cart";
          return;
        }
      }
    } catch (err) {
      console.error("Error adding/updating cart:", err);

      // Server-side fallback. If /cart/add.js never succeeded, Shopify's
      // native cart permalink route can add the items itself.
      window.location.href = err.itemsAddedToCart
        ? "/cart"
        : addSkipShopPayParam(href);
      return;
    } finally {
      // Loader OFF always runs
      setDrawerLoading(drawer, false);
      window.clearTimeout(unlockTimer);
      setCheckoutInFlight(anchor, false);
    }
  }

  // HOTFIX (2026-05-07): Added missing `return` so `await clearCart()` actually waits.
  // Previously the fetch was fire-and-forget, creating a race condition with the
  // subsequent /cart/add.js call that could empty the cart after items were added.
  async function clearCart() {
    const res = await fetchWithRetry("/cart/clear.js", {
      method: "POST",
      credentials: "same-origin",
    });

    if (res.ok) {
      return;
    }

    let errorData = null;
    try {
      errorData = await res.json();
    } catch (_) {}

    throw new Error(
      (errorData && errorData.description) || "Request failed: /cart/clear.js",
    );
  }

  window.addEventListener("pageshow", function () {
    document.querySelectorAll("[data-checkout-in-flight]").forEach((anchor) => {
      anchor.dataset.checkoutInFlight = "";
      anchor.classList.remove("is-checkout-in-flight");
    });
  });

  // Capture-phase listener so we intercept before other scripts can hijack navigation
  document.addEventListener(
    "click",
    function (e) {
      const anchor =
        e.target && e.target.closest
          ? e.target.closest("a[href]")
          : null;

      if (!anchor) return;

      if (shouldIgnoreClick(e, anchor)) return true;

      // If anchor explicitly wants new tab/window, don't intercept
      const target = (anchor.getAttribute("target") || "").toLowerCase();
      if (target === "_blank") return true;

      handleClick(e, anchor);
    },
    {
      capture: true,
      passive: false,
    },
  );
})();